import moment from 'moment';
import { http } from '@/http';
import {
    ActionGetIPAddress,
    getBrazilianHolidays,
    isBusinessDay,
} from '@/utils/helpers';
import { isPixPaymentEnabled, isPixPaymentMethod } from '../utils/paymentFlags';
import * as types from './mutation-types';

const MAIN_INSTALLMENTS = [1, 3, 6, 9];

function buildOffersList(offers) {
    return offers.map(offer => ({
        name: offer.installmentNumber === 1 ? 'À vista por' : `Parcelado - ${offer.installmentNumber}x de`,
        ...offer,
    }));
}

function agreementStatusIsSuccess(value) {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase();
        return v === 'true' || v === '1' || v === 's';
    }
    return false;
}

/**
 * Normaliza o retorno de boticario-eudora/agreement (payload aninhado em `response` ou achatado).
 */
function parseBoticarioEudoraAgreementResult(result) {
    if (result == null || typeof result !== 'object') {
        return { inner: null, ok: false, errorMessage: null };
    }
    const inner = result.response != null && typeof result.response === 'object'
        ? result.response
        : result;
    const statusValue = inner.status !== undefined ? inner.status : result.status;
    const statusOk = agreementStatusIsSuccess(statusValue);
    const agreementId = inner.agreementId != null ? String(inner.agreementId).trim() : '';
    const hasPaymentLine = Boolean(
        agreementId
        || (inner.pixCopyAndPast && String(inner.pixCopyAndPast).trim())
        || (inner.digitableLine && String(inner.digitableLine).trim())
        || (inner.urlCard && String(inner.urlCard).trim()),
    );
    const errMsg = inner.errorMessage || result.errorMessage || null;
    const ok = (statusOk && hasPaymentLine)
        || (hasPaymentLine && !errMsg && (statusValue === undefined || statusValue === null));
    return { inner, ok, errorMessage: errMsg };
}

// DX-3593: mensagens de negócio novas do barramento (HTTP 400, ERR_BUSINESS_RULE) pro
// fechamento de acordo Boticário, traduzidas pro usuário final.
const BOTICARIO_AGREEMENT_ERROR_MESSAGES = {
    'installments with different documents': 'Não é possível negociar parcelas do CPF e do CNPJ juntas. Volte e escolha uma única dívida.',
    'invalid document': 'Não foi possível confirmar o documento dessa dívida. Atualize a página e tente novamente.',
};

/**
 * Extrai uma mensagem de erro amigável de uma rejeição HTTP (axios) do fluxo
 * boticario-eudora/agreement. Sem isso, o interceptor global rejeita com o `response`
 * cru (sem `.message`), e o erro específico do backend nunca chega até a tela.
 */
function extractBoticarioEudoraErrorMessage(e, fallback = 'Não foi possível confirmar na tela. Atualize a página antes de tentar de novo; o acordo pode já ter sido registrado.') {
    if (e instanceof Error && e.message) return e.message;
    const raw = (e && typeof e === 'object' && e.data && (e.data.message || e.data.error)) || null;
    if (typeof raw === 'string' && raw) {
        return BOTICARIO_AGREEMENT_ERROR_MESSAGES[raw] || raw;
    }
    return fallback;
}

function computeDates(debts, offerKey, maximumDateFallback) {
    const now = moment();
    const maximumDate = debts[0] && debts[0][offerKey] && debts[0][offerKey][0]
        ? moment(debts[0][offerKey][0].maximumDueDate, 'YYYY-MM-DD')
        : moment().add(maximumDateFallback || 30, 'days');

    const holidays = getBrazilianHolidays(now.year());
    const dates = [];
    let count = 0;

    while (count < 5) {
        if (now.isAfter(maximumDate)) break;
        if (isBusinessDay(now, holidays)) {
            dates.push(now.format('DD/MM/YYYY'));
            count++;
        }
        now.add(1, 'day');
    }
    return dates;
}

function strTruthyApi(v) {
    if (v == null) return false;
    const s = String(v).trim();
    if (!s) return false;
    if (s.toLowerCase() === 'null') return false;
    return true;
}

/** PHP setAgreementBoticario exige string; Siscobra às vezes omite con_cod (null). */
function agreementStringField(value) {
    if (value == null) return '';
    return String(value);
}

function stringifyLogKeyPart(value) {
    if (Array.isArray(value)) return value.join('|');
    if (value && typeof value === 'object') return JSON.stringify(value);
    return String(value ?? '');
}

function buildBoticarioViewOffersLogKey(state, debt) {
    return [
        state.clientIdBoticario,
        debt?.product,
        debt?.contracts ?? debt?.contract,
    ].map(stringifyLogKeyPart).join(':');
}

/**
 * ActionNegotiationSelectEudora() não recebe um "debt" específico (processa
 * state.offersEudora/debtsEudora inteiro de uma vez, diferente do fluxo
 * Boticário que seleciona um debt por vez) — dedup por clientId + produtos
 * envolvidos, não por debt individual.
 */
function buildEudoraViewOffersLogKey(state) {
    const products = (state.debtsEudora || []).map((debt) => debt?.product);
    return [state.clientIdEudora, products].map(stringifyLogKeyPart).join(':');
}

function ensureHttpUrl(s) {
    const t = String(s).trim();
    if (!t) return '';
    if (/^https?:\/\//i.test(t)) return t;
    return `https://${t}`;
}

function buildAgreementBillOptions(inner) {
    if (!inner || typeof inner !== 'object') return [];
    const opts = [];
    const billPdf = inner.billPdf;
    const pixPdf = inner.pixPdf;
    if (strTruthyApi(inner.linkCard)) {
        opts.push({
            key: 'card',
            label: 'Cartão',
            linkCard: ensureHttpUrl(inner.linkCard),
            billPdf,
            pixPdf,
        });
    }
    const pix = inner.pixCopyAndPast || inner.pixCopyPaste || inner.pixCopyAndPaste;
    if (strTruthyApi(pix) && isPixPaymentEnabled()) {
        opts.push({
            key: 'pix',
            label: 'Pix',
            pixCopyAndPast: String(pix).trim(),
            billPdf,
            pixPdf,
        });
    }
    const hasLine = strTruthyApi(inner.digitableLine);
    const hasLinkBill = strTruthyApi(inner.linkBill);
    const lineStr = hasLine ? String(inner.digitableLine).trim() : '';
    const linkStr = hasLinkBill ? ensureHttpUrl(inner.linkBill) : '';

    if (hasLine && hasLinkBill) {
        opts.push({
            key: 'boleto',
            label: 'Boleto',
            boletoSubKey: 'line',
            digitableLine: lineStr,
            linkBill: '',
            billValue: inner.billValue,
            billDueDate: inner.billDueDate,
            billPdf,
            pixPdf,
        });
        opts.push({
            key: 'boleto',
            label: 'Link do boleto',
            boletoSubKey: 'link',
            digitableLine: '',
            linkBill: linkStr,
            billValue: inner.billValue,
            billDueDate: inner.billDueDate,
            billPdf,
            pixPdf,
        });
    } else if (hasLine) {
        opts.push({
            key: 'boleto',
            label: 'Boleto',
            boletoSubKey: null,
            digitableLine: lineStr,
            linkBill: '',
            billValue: inner.billValue,
            billDueDate: inner.billDueDate,
            billPdf,
            pixPdf,
        });
    } else if (hasLinkBill) {
        opts.push({
            key: 'boleto',
            label: 'Boleto',
            boletoSubKey: null,
            digitableLine: '',
            linkBill: linkStr,
            billValue: inner.billValue,
            billDueDate: inner.billDueDate,
            billPdf,
            pixPdf,
        });
    }

    // Se o acordo antigo só tem PIX, mantém a opção para a pessoa conseguir pagar.
    if (!isPixPaymentEnabled() && opts.length === 0 && strTruthyApi(pix)) {
        opts.push({
            key: 'pix',
            label: 'Pix',
            pixCopyAndPast: String(pix).trim(),
            billPdf,
            pixPdf,
        });
    }

    return opts;
}

function dueDateForAgreementBillApi(item) {
    const raw = item.earliestDueDate || item.dueDate;
    if (raw == null || raw === '') {
        throw new Error('Data de vencimento não disponível para este acordo.');
    }
    const m = moment(raw, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
    if (m.isValid()) return m.format('YYYY-MM-DD');
    const m2 = moment(raw);
    if (!m2.isValid()) throw new Error('Data de vencimento inválida.');
    return m2.format('YYYY-MM-DD');
}

export const ActionNegotiationGetIPAddress = async ({ commit }) => {
    let maxAttempts = 10;
    let attempts = 0;

    while (attempts < maxAttempts) {
        try {
            const ip = await ActionGetIPAddress();
            if (ip !== null) {
                commit(types.SET_IP_USER, ip);
                localStorage.setItem('ipUser', ip);
            }
            break;
        } catch {
            attempts++;
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
    }
};

export const ActionNegotiationGetDebts = async ({ commit, state, dispatch }, dueDateFrom) => {
    const userIDStorage = localStorage.getItem('UserID');
    let datePayload = null;

    if (dueDateFrom === 'Boticario') {
        datePayload = state.dueDateBoticario;
    } else if (dueDateFrom === 'Eudora') {
        datePayload = state.dueDateEudora;
    }

    const result = await dispatch('meusContratos/ActionGetDebtsBoticarioAndEudora', {
        IP: state.ipUser ?? '',
        DeviceName: process.env.VUE_APP_API_DEVICE,
        OS: navigator.platform,
        DocNumber: state.docNumber,
        clientId: null,
        dueDate: moment(datePayload ?? moment(), 'DD/MM/YYYY').format('YYYY-MM-DD'),
        UserID: state.userAdmId ?? userIDStorage,
        tag: localStorage.getItem('tag'),
        from: dueDateFrom,
        advisory: localStorage.getItem('advisory'),
    }, { root: true });

    // DX-3593: o Boticário pode mandar o mesmo produto duas vezes agora — uma dívida no CPF
    // da revendedora, outra no CNPJ da empresa dela — então debtsBoticario virou lista.
    let debtsBoticario = [];

    if (Array.isArray(result.debtsBoticario)) {
        debtsBoticario = result.debtsBoticario;
    } else if (result.debtsBoticario && typeof result.debtsBoticario === 'object') {
        // Compatibilidade com o formato antigo (objeto único, pré-DX-3593).
        debtsBoticario = [result.debtsBoticario];
    }

    // preventive virou campo próprio da resposta — não dá mais pra tirar de "o" item único.
    const preventiveBoticario = result.preventiveBoticario ?? debtsBoticario[0]?.preventive ?? [];

    commit(types.SET_DEBTS_BOTICARIO, debtsBoticario);
    commit(types.SET_PREVENTIVE_BOTICARIO, preventiveBoticario);
    commit(types.SET_CLIENT_ID_BOTICARIO, result.clientIdBoticario);

    if (debtsBoticario.length > 0) {
        const offersBoticario = debtsBoticario.flatMap(debt => debt.offersBoticario || []);
        commit(types.SET_OFFERS_BOTICARIO, offersBoticario);
        commit(types.SET_LATE_DATE_BOTICARIO, moment(result.earliestDueDateBoticario).format('DD/MM/YYYY '));

        const dates = computeDates(debtsBoticario, 'offersBoticario');
        const current = state.dueDateBoticario;
        commit(types.SET_DUE_DATES_BOTICARIO, dates);
        commit(types.SET_DUE_DATE_BOTICARIO, (current && dates.includes(current)) ? current : dates[0]);
    } else {
        commit(types.SET_OFFERS_BOTICARIO, []);
    }

    if (preventiveBoticario.length > 0) {
        commit(types.SET_HAS_AGREEMENT_BOTICARIO, true);
    }

    let debtsEudora = [];
    let preventiveEudora = [];

    if (Array.isArray(result.debtsEudora)) {
        debtsEudora = [];
        preventiveEudora = [];
    } else if (result.debtsEudora && typeof result.debtsEudora === 'object') {
        debtsEudora = [result.debtsEudora];
        preventiveEudora = result.debtsEudora.preventive || [];
    }

    commit(types.SET_DEBTS_EUDORA, debtsEudora);
    commit(types.SET_PREVENTIVE_EUDORA, preventiveEudora);
    commit(types.SET_CLIENT_ID_EUDORA, result.clientIdEudora);

    if (debtsEudora.length > 0) {
        const offersEudora = debtsEudora.flatMap(debt => debt.offersEudora || []);
        commit(types.SET_OFFERS_EUDORA, offersEudora);
        commit(types.SET_LATE_DATE_EUDORA, moment(result.earliestDueDateEudora).format('DD/MM/YYYY '));

        if (offersEudora.length > 0) {
            const dates = computeDates(debtsEudora, 'offersEudora');
            const current = state.dueDateEudora;
            commit(types.SET_DUE_DATES_EUDORA, dates);
            commit(types.SET_DUE_DATE_EUDORA, (current && dates.includes(current)) ? current : dates[0]);
        }
    } else {
        commit(types.SET_OFFERS_EUDORA, []);
    }

    if (preventiveEudora.length > 0) {
        commit(types.SET_HAS_AGREEMENT_EUDORA, true);
    }

    const hasNoBoticario = debtsBoticario.length <= 0 && preventiveBoticario.length <= 0;
    const hasNoEudora = debtsEudora.length <= 0 && preventiveEudora.length <= 0;

    commit(types.SET_HAS_NO_BOTICARIO_DATA, hasNoBoticario);
    commit(types.SET_HAS_NO_EUDORA_DATA, hasNoEudora);

    dispatch('ActionNegotiationRefreshBoticario');
    dispatch('ActionNegotiationRefreshEudora');

    if (hasNoBoticario && hasNoEudora) {
        commit(types.SET_NOT_LOGGED_TEXT, 'Nenhuma oferta encontrada.');
        throw new Error('Nenhuma oferta encontrada.');
    }
};

export const ActionNegotiationVerifyDirectAccess = async ({ commit, state, dispatch }, { encryptedData, queryType, shortCode = null }) => {
    commit(types.SET_LOADING, true);

    if (state.alreadyVerified) return;

    const userIDStorage = localStorage.getItem('UserID');
    const userAdm = JSON.parse(localStorage.getItem('userAdm'));

    const response = await dispatch('auth/ActionGetLinkDirectAccess', {
        encryptedData,
        queryType,
        shortCode,
        IP: state.ipUser ?? '',
        DeviceName: process.env.VUE_APP_API_DEVICE,
        OS: navigator.platform,
        UserID: state.userAdmId ?? userIDStorage,
        UserFullName: userAdm ? userAdm.UserFullName : null,
        tag: localStorage.getItem('tag'),
    }, { root: true });

    commit(types.SET_USER_ADM_ID, response.userAdmId);
    commit(types.SET_DOC_NUMBER, response.docNumber);
    commit(types.SET_DEBTOR_ID, response.debtorId);

    return response;
};

export const ActionNegotiationSelectBoticario = ({ commit, state, dispatch }, debtBoticario) => {
    commit(types.SET_DEBT_BOTICARIO, debtBoticario);

    // DX-3593: o mesmo `product` pode aparecer em duas dívidas (CPF e CNPJ da revendedora).
    // Filtrar a lista achatada `state.offersBoticario` só por product misturaria oferta de
    // uma titularidade com a da outra. O item selecionado já carrega as ofertas certas dele.
    const filteredOffers = Array.isArray(debtBoticario.offersBoticario) ? debtBoticario.offersBoticario : [];
    const negotiationOffers = buildOffersList(filteredOffers);
    const mainOffers = negotiationOffers.filter(o => MAIN_INSTALLMENTS.includes(o.installmentNumber));
    const contracts = debtBoticario.contracts ?? debtBoticario.contract ?? filteredOffers[0]?.contracts ?? null;
    const viewOffersLogKey = buildBoticarioViewOffersLogKey(state, {
        ...debtBoticario,
        contracts,
    });

    if (!state.viewedOffersBoticarioLogKeys.includes(viewOffersLogKey)) {
        commit(types.ADD_VIEWED_OFFERS_BOTICARIO_LOG_KEY, viewOffersLogKey);
        const userIDStorage = localStorage.getItem('UserID');

        dispatch('meusContratos/ActionViewOffersBoticarioAndEudora', {
            IP: state.ipUser ?? '',
            DeviceName: process.env.VUE_APP_API_DEVICE,
            OS: navigator.platform,
            DocNumber: state.docNumber,
            UserID: state.userAdmId ?? userIDStorage,
            tag: localStorage.getItem('tag'),
            advisory: localStorage.getItem('advisory'),
            clientIdBoticario: state.clientIdBoticario,
            product: debtBoticario.product,
            contracts,
            NotesJson: {
                clientIdBoticario: state.clientIdBoticario,
                product: debtBoticario.product,
                contracts,
            },
        }, { root: true }).catch((err) => {
            console.warn('[negotiation] Log Visualizou Ofertas Boticário ignorado:', err);
        });
    }

    commit(types.SET_OFFERS_NEGOTIATION_BOTICARIO, negotiationOffers);
    commit(types.SET_MAIN_OFFERS_BOTICARIO, mainOffers);

    // Garante que o seletor de vencimento sempre tenha datas válidas. Como
    // o RESET_NEGOTIATION_FLOW preserva dueDates/dueDate (para evitar refetch
    // no Voltar), aqui recomputamos se o estado estiver vazio — isso protege
    // contra cenários de sessão longa sem refetch.
    if (!Array.isArray(state.dueDatesBoticario) || state.dueDatesBoticario.length === 0) {
        const dates = computeDates([debtBoticario], 'offersBoticario');
        if (dates.length > 0) {
            const current = state.dueDateBoticario;
            commit(types.SET_DUE_DATES_BOTICARIO, dates);
            commit(types.SET_DUE_DATE_BOTICARIO, (current && dates.includes(current)) ? current : dates[0]);
        }
    }

    commit(types.SET_VIEW, 'negotiationBoticario');
};

export const ActionNegotiationSelectEudora = ({ commit, state, dispatch }) => {
    if (!state.offersEudora || state.offersEudora.length === 0) return;

    const negotiationOffers = buildOffersList(state.offersEudora);
    const mainOffers = negotiationOffers.filter(o => MAIN_INSTALLMENTS.includes(o.installmentNumber));

    const viewOffersLogKey = buildEudoraViewOffersLogKey(state);
    if (!state.viewedOffersEudoraLogKeys.includes(viewOffersLogKey)) {
        commit(types.ADD_VIEWED_OFFERS_EUDORA_LOG_KEY, viewOffersLogKey);
        const userIDStorage = localStorage.getItem('UserID');
        const products = (state.debtsEudora || []).map((debt) => debt?.product);

        dispatch('meusContratos/ActionViewOffersBoticarioAndEudora', {
            IP: state.ipUser ?? '',
            DeviceName: process.env.VUE_APP_API_DEVICE,
            OS: navigator.platform,
            DocNumber: state.docNumber,
            UserID: state.userAdmId ?? userIDStorage,
            tag: localStorage.getItem('tag'),
            advisory: localStorage.getItem('advisory'),
            clientIdEudora: state.clientIdEudora,
            product: products,
            NotesJson: {
                clientIdEudora: state.clientIdEudora,
                products,
            },
        }, { root: true }).catch((err) => {
            console.warn('[negotiation] Log Visualizou Ofertas Eudora ignorado:', err);
        });
    }

    commit(types.SET_OFFERS_NEGOTIATION_EUDORA, negotiationOffers);
    commit(types.SET_MAIN_OFFERS_EUDORA, mainOffers);

    if (!Array.isArray(state.dueDatesEudora) || state.dueDatesEudora.length === 0) {
        const debtsEudora = Array.isArray(state.debtsEudora) ? state.debtsEudora : [];
        if (debtsEudora.length > 0) {
            const dates = computeDates(debtsEudora, 'offersEudora');
            if (dates.length > 0) {
                const current = state.dueDateEudora;
                commit(types.SET_DUE_DATES_EUDORA, dates);
                commit(types.SET_DUE_DATE_EUDORA, (current && dates.includes(current)) ? current : dates[0]);
            }
        }
    }

    commit(types.SET_VIEW, 'negotiationEudora');
};

export const ActionNegotiationRefreshBoticario = ({ commit, state }) => {
    if (state.currentView !== 'negotiationBoticario' || !state.debtBoticario || !state.debtsBoticario || state.debtsBoticario.length === 0) return;

    const { product, document: debtDocument } = state.debtBoticario;
    // DX-3593: casa por product + document — duas dívidas podem ter o mesmo product (CPF e
    // CNPJ da revendedora). Fallback só por product cobre resposta antiga sem o campo.
    const updatedDebt = state.debtsBoticario.find(d => d.product === product && d.document === debtDocument)
        ?? state.debtsBoticario.find(d => d.product === product);
    if (updatedDebt) commit(types.SET_DEBT_BOTICARIO, updatedDebt);

    const filteredOffers = Array.isArray(updatedDebt?.offersBoticario) ? updatedDebt.offersBoticario : [];
    const negotiationOffers = buildOffersList(filteredOffers);
    const mainOffers = negotiationOffers.filter(o => MAIN_INSTALLMENTS.includes(o.installmentNumber));

    commit(types.SET_OFFERS_NEGOTIATION_BOTICARIO, negotiationOffers);
    commit(types.SET_MAIN_OFFERS_BOTICARIO, mainOffers);

    if (negotiationOffers.length > 0) {
        const currentInstallment = state.selectedOfferBoticario?.installmentNumber;
        const sameOption = currentInstallment != null && negotiationOffers.find(o => Number(o.installmentNumber) === Number(currentInstallment));
        commit(types.SET_SELECTED_OFFER_BOTICARIO, sameOption || negotiationOffers[0]);
    }
};

export const ActionNegotiationRefreshEudora = ({ commit, state }) => {
    if (state.currentView !== 'negotiationEudora' || !state.offersEudora || state.offersEudora.length === 0) return;

    const negotiationOffers = buildOffersList(state.offersEudora);
    const mainOffers = negotiationOffers.filter(o => MAIN_INSTALLMENTS.includes(o.installmentNumber));

    commit(types.SET_OFFERS_NEGOTIATION_EUDORA, negotiationOffers);
    commit(types.SET_MAIN_OFFERS_EUDORA, mainOffers);

    if (negotiationOffers.length > 0) {
        const currentInstallment = state.selectedOfferEudora?.installmentNumber;
        const sameOption = currentInstallment != null && negotiationOffers.find(o => Number(o.installmentNumber) === Number(currentInstallment));
        commit(types.SET_SELECTED_OFFER_EUDORA, sameOption || negotiationOffers[0]);
    }
};

// Ao trocar a data de vencimento usamos o modo "offers-only" do backend, que pula as
// chamadas de creditor + debts e refaz apenas a /offers (única coisa que muda com a
// data). Isso elimina o gargalo principal de troca de data. Em caso de falha do
// caminho rápido, caímos no fluxo completo (legado) automaticamente.
export const ActionNegotiationChangeDueDateBoticario = async ({ commit, dispatch }, value) => {
    commit(types.SET_LOADING_DUE_DATE, true);
    commit(types.SET_DUE_DATE_BOTICARIO, value);
    try {
        const okFastPath = await dispatch('ActionNegotiationRefreshOffers', {
            brand: 'Boticario',
        }).catch(() => false);
        if (!okFastPath) {
            await dispatch('ActionNegotiationGetDebts', 'Boticario');
        }
    } finally {
        commit(types.SET_LOADING_DUE_DATE, false);
    }
};

export const ActionNegotiationChangeDueDateEudora = async ({ commit, dispatch }, value) => {
    commit(types.SET_LOADING_DUE_DATE, true);
    commit(types.SET_DUE_DATE_EUDORA, value);
    try {
        const okFastPath = await dispatch('ActionNegotiationRefreshOffers', {
            brand: 'Eudora',
        }).catch(() => false);
        if (!okFastPath) {
            await dispatch('ActionNegotiationGetDebts', 'Eudora');
        }
    } finally {
        commit(types.SET_LOADING_DUE_DATE, false);
    }
};

/**
 * Atualiza somente as ofertas (offers-only) sem refazer creditor + debts.
 *
 * Usa o endpoint `boticario-eudora/debts` com `mode=offersOnly`, passando a dívida
 * cacheada no estado. O backend devolve o mesmo formato de payload de uma
 * dívida, então conseguimos aplicar via mutations existentes.
 *
 * @returns {Promise<boolean>} true quando o caminho rápido foi aplicado com sucesso.
 */
export const ActionNegotiationRefreshOffers = async ({ commit, state, dispatch }, { brand }) => {
    if (brand !== 'Boticario' && brand !== 'Eudora') return false;

    // DX-3593: a dívida em negociação (não necessariamente a primeira da lista — pode haver
    // uma dívida no CPF e outra no CNPJ da revendedora ao mesmo tempo).
    const cachedBoticario = state.debtBoticario
        ?? (Array.isArray(state.debtsBoticario) && state.debtsBoticario[0] ? state.debtsBoticario[0] : null);
    const cachedEudora = Array.isArray(state.debtsEudora) && state.debtsEudora[0]
        ? state.debtsEudora[0]
        : null;

    if (brand === 'Boticario' && (!state.clientIdBoticario || !cachedBoticario)) return false;
    if (brand === 'Eudora' && (!state.clientIdEudora || !cachedEudora)) return false;

    const datePayload = brand === 'Boticario' ? state.dueDateBoticario : state.dueDateEudora;
    const userIDStorage = localStorage.getItem('UserID');

    const payload = {
        IP: state.ipUser ?? '',
        DeviceName: process.env.VUE_APP_API_DEVICE,
        OS: navigator.platform,
        DocNumber: state.docNumber,
        clientId: null,
        clientIdBoticario: state.clientIdBoticario,
        clientIdEudora: state.clientIdEudora,
        dueDate: moment(datePayload ?? moment(), 'DD/MM/YYYY').format('YYYY-MM-DD'),
        UserID: state.userAdmId ?? userIDStorage,
        tag: localStorage.getItem('tag'),
        from: brand,
        advisory: localStorage.getItem('advisory'),
        mode: 'offersOnly',
        offersOnly: true,
        cachedDebtBoticario: brand === 'Boticario' ? cachedBoticario : null,
        cachedDebtsEudora: brand === 'Eudora' ? cachedEudora : null,
    };

    let result;
    try {
        const { data } = await http.post('boticario-eudora/debts', payload);
        result = data && data.results ? data.results : data;
    } catch (e) {
        return false;
    }

    if (!result || result.mode !== (brand === 'Boticario' ? 'offersOnlyBoticario' : 'offersOnlyEudora')) {
        return false;
    }

    if (brand === 'Boticario') {
        // DX-3593: o backend agora devolve debtsBoticario como lista mesmo no modo
        // offersOnly (consistente com o fluxo completo) — pega o item único dela.
        const debt = Array.isArray(result.debtsBoticario) ? result.debtsBoticario[0] : result.debtsBoticario;
        if (!debt || typeof debt !== 'object') return false;

        const previousDebts = Array.isArray(state.debtsBoticario) ? state.debtsBoticario : [];
        const previousMatch = previousDebts.find(d => d.product === debt.product && d.document === debt.document)
            ?? previousDebts[0]
            ?? null;

        // Preserva preventive existente (offers-only não modifica acordos).
        const debtMerged = {
            ...debt,
            preventive: debt.preventive ?? previousMatch?.preventive ?? state.preventiveBoticario ?? [],
        };

        // DX-3593: atualiza só o item refeito — pode haver outra dívida (CPF ou CNPJ) em
        // paralelo na lista, que não pode ser apagada por um refresh deste item.
        const jaExistiaNaLista = previousDebts.some(d => d.product === debtMerged.product && d.document === debtMerged.document);
        const debtsAtualizados = jaExistiaNaLista
            ? previousDebts.map(d => (d.product === debtMerged.product && d.document === debtMerged.document) ? debtMerged : d)
            : [...previousDebts, debtMerged];

        commit(types.SET_DEBTS_BOTICARIO, debtsAtualizados);
        const offersBoticario = debtsAtualizados.flatMap(d => Array.isArray(d.offersBoticario) ? d.offersBoticario : []);
        commit(types.SET_OFFERS_BOTICARIO, offersBoticario);

        if (result.earliestDueDateBoticario) {
            commit(types.SET_LATE_DATE_BOTICARIO, moment(result.earliestDueDateBoticario).format('DD/MM/YYYY '));
        }

        commit(types.SET_DEBT_BOTICARIO, debtMerged);

        // Reusa a mesma lógica do refresh existente para evitar drift.
        await dispatch('ActionNegotiationRefreshBoticario');
        return true;
    }

    const debtE = result.debtsEudora;
    if (!debtE || typeof debtE !== 'object') return false;

    const previousFirstDebtE = Array.isArray(state.debtsEudora) ? state.debtsEudora[0] : null;
    const debtEMerged = {
        ...debtE,
        preventive: debtE.preventive ?? previousFirstDebtE?.preventive ?? state.preventiveEudora ?? [],
    };

    commit(types.SET_DEBTS_EUDORA, [debtEMerged]);
    const offersEudora = Array.isArray(debtEMerged.offersEudora) ? debtEMerged.offersEudora : [];
    commit(types.SET_OFFERS_EUDORA, offersEudora);

    if (result.earliestDueDateEudora) {
        commit(types.SET_LATE_DATE_EUDORA, moment(result.earliestDueDateEudora).format('DD/MM/YYYY '));
    }

    await dispatch('ActionNegotiationRefreshEudora');
    return true;
};

export const ActionNegotiationPrepareAgreement = ({ commit, state }, offer) => {
    // DX-3593: carimba de qual documento (CPF ou CNPJ da revendedora) veio a oferta
    // escolhida, pro backend saber qual titularidade está sendo negociada no acordo.
    commit(types.SET_PREPARE_AGREEMENT, { ...offer, debtDocument: state.debtBoticario?.document ?? null });
    commit(types.SET_SELECTED_PAYMENT_METHOD, null);
    const nextView = state.currentView === 'negotiationEudora' ? 'paymentEudora' : 'paymentBoticario';
    commit(types.SET_VIEW, nextView);
};

export const ActionNegotiationSelectPaymentMethod = ({ commit }, method) => {
    if (isPixPaymentMethod(method) && !isPixPaymentEnabled()) return;
    commit(types.SET_SELECTED_PAYMENT_METHOD, method);
};

function assertPixPaymentAllowed(method) {
    if (isPixPaymentMethod(method) && !isPixPaymentEnabled()) {
        throw new Error('PIX temporariamente indisponível. Escolha boleto ou cartão.');
    }
}

export const ActionNegotiationSetAgreementBoticario = async ({ commit, state, dispatch }) => {
    commit(types.SET_LOADING_SET_AGREEMENT, true);
    try {
        assertPixPaymentAllowed(state.selectedPaymentMethod);
        const userIDStorage = localStorage.getItem('UserID');
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        const storeUser = user;

        let result;
        try {
            result = await dispatch('meusContratos/ActionSetAgreementBoticarioAndEudora', {
                IP: state.ipUser ?? '',
                OS: navigator.platform,
                DeviceName: process.env.VUE_APP_API_DEVICE,
                DocNumber: state.docNumber,
                product: state.prepareToSetAgreement.product,
                negotiationForm: state.prepareToSetAgreement.negotiationForm,
                agreementRule: state.prepareToSetAgreement.agreementRule,
                contracts: agreementStringField(state.prepareToSetAgreement.contracts),
                installments: agreementStringField(state.prepareToSetAgreement.installments),
                dueDate: state.prepareToSetAgreement.dueDate,
                installmentNumber: state.prepareToSetAgreement.installmentNumber,
                inputValue: state.prepareToSetAgreement.inputValue,
                sendWhats: 'N',
                sendEmail: 'N',
                clientIdBoticario: state.clientIdBoticario,
                negotiationFormPg: state.selectedPaymentMethod,
                installmentValue: state.prepareToSetAgreement.installmentValue,
                UserID: state.userAdmId ?? userIDStorage,
                tag: localStorage.getItem('tag'),
                boticarioOrEudora: 'Boticario',
                advisory: localStorage.getItem('advisory'),
                PhoneNumber: storeUser?.PhoneNumber,
                Email: storeUser?.Email,
                // DX-3593: qual documento (CPF ou CNPJ da revendedora) está sendo fechado —
                // evita o barramento misturar parcela de uma titularidade com a da outra.
                debtDocument: state.prepareToSetAgreement.debtDocument ?? state.debtBoticario?.document ?? null,
            }, { root: true });
        } catch (httpErr) {
            // DX-3593: o barramento agora pode recusar com 400/ERR_BUSINESS_RULE
            // ("installments with different documents", "invalid document") — sem isto, o
            // axios rejeita com o `response` cru (sem `.message`), e o catch do componente
            // sempre caía na mensagem genérica, escondendo o motivo real do erro 400.
            throw new Error(extractBoticarioEudoraErrorMessage(httpErr));
        }

        const { inner, ok, errorMessage } = parseBoticarioEudoraAgreementResult(result);
        if (!inner || !ok) {
            throw new Error(errorMessage || 'Erro ao fechar acordo.');
        }

        const agreement = { ...inner, dueDate: state.prepareToSetAgreement.dueDate };
        commit(types.SET_AGREEMENT, agreement);

        const linha = (agreement.digitableLine && String(agreement.digitableLine).trim()) || agreement.pixCopyAndPast || (agreement.urlCard && String(agreement.urlCard).trim()) || '';
        try {
            await dispatch('ActionNegotiationSendSms', { linhaDigitavel: linha, from: 'Boticario' });
        } catch (smsErr) {
            console.warn('[negotiation] SMS pós-acordo Boticário ignorado:', smsErr);
        }

        return agreement;
    } finally {
        commit(types.SET_LOADING_SET_AGREEMENT, false);
    }
};

export const ActionNegotiationSetAgreementEudora = async ({ commit, state, dispatch }) => {
    commit(types.SET_LOADING_SET_AGREEMENT, true);
    try {
        assertPixPaymentAllowed(state.selectedPaymentMethod);
        const userIDStorage = localStorage.getItem('UserID');
        const user = JSON.parse(localStorage.getItem('user') || 'null');

        const result = await dispatch('meusContratos/ActionSetAgreementBoticarioAndEudora', {
            IP: state.ipUser ?? '',
            OS: navigator.platform,
            DeviceName: process.env.VUE_APP_API_DEVICE,
            dueDate: state.prepareToSetAgreement.dueDate,
            product: state.prepareToSetAgreement.product,
            clientIdEudora: state.clientIdEudora,
            contracts: agreementStringField(state.prepareToSetAgreement.contracts),
            installments: agreementStringField(state.prepareToSetAgreement.installments),
            agreementRule: state.prepareToSetAgreement.agreementRule,
            negotiationForm: state.prepareToSetAgreement.negotiationForm,
            negotiationFormPg: state.selectedPaymentMethod,
            installmentNumber: state.prepareToSetAgreement.installmentNumber,
            DocNumber: state.docNumber,
            installmentValue: state.prepareToSetAgreement.installmentValue,
            UserID: state.userAdmId ?? userIDStorage,
            tag: localStorage.getItem('tag'),
            boticarioOrEudora: 'Eudora',
            advisory: localStorage.getItem('advisory'),
            PhoneNumber: user?.PhoneNumber,
            Email: user?.Email,
        }, { root: true });

        const { inner, ok, errorMessage } = parseBoticarioEudoraAgreementResult(result);
        if (!inner || !ok) {
            throw new Error(errorMessage || 'Erro ao fechar acordo.');
        }

        const agreement = { ...inner, dueDate: state.prepareToSetAgreement.dueDate };
        commit(types.SET_AGREEMENT, agreement);

        const linha = (agreement.digitableLine && String(agreement.digitableLine).trim()) || agreement.pixCopyAndPast || (agreement.urlCard && String(agreement.urlCard).trim()) || '';
        try {
            await dispatch('ActionNegotiationSendSms', { linhaDigitavel: linha, from: 'Eudora' });
        } catch (smsErr) {
            console.warn('[negotiation] SMS pós-acordo Eudora ignorado:', smsErr);
        }

        return agreement;
    } finally {
        commit(types.SET_LOADING_SET_AGREEMENT, false);
    }
};

export const ActionNegotiationSendSms = async ({ state, dispatch }, { linhaDigitavel, from }) => {
    const userIDStorage = localStorage.getItem('UserID');
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    await dispatch('meusBoletos/ActionSendEmailOrSmsText', {
        LinhaDigitavel: linhaDigitavel,
        Email: '',
        PhoneNumber: user?.PhoneNumber != null ? user.PhoneNumber : user?.MobileNumber,
        type: 'sms',
        OS: navigator.platform,
        IP: state.ipUser ?? '',
        UserID: state.userAdmId ?? userIDStorage,
        tag: localStorage.getItem('tag'),
        from,
    }, { root: true });
};

function digitsOnlyPhone(val) {
    return String(val || '').replace(/\D/g, '');
}

export const ActionNegotiationSendEmailOrWhatsapp = async (
    { state, dispatch },
    { sendEmail, sendWhatsapp, from, phoneNumber, agreementId: agreementIdOverride, dueDate: dueDateOverride },
) => {
    const userIDStorage = localStorage.getItem('UserID');
    const user = JSON.parse(localStorage.getItem('user') || 'null');

    const clientId = from === 'Boticario' ? state.clientIdBoticario : state.clientIdEudora;

    let phoneForApi = '';
    if (sendWhatsapp) {
        const override = digitsOnlyPhone(phoneNumber);
        if (override.length >= 10) {
            phoneForApi = override;
        } else {
            const fromUser = user?.PhoneNumber != null ? user.PhoneNumber : user?.MobileNumber;
            phoneForApi = String(fromUser || '').replace(/\D/g, '');
        }
    }

    const agreementIdFromState = state.agreement && state.agreement.agreementId != null
        ? String(state.agreement.agreementId).trim()
        : '';
    const dueDateFromState = state.agreement && state.agreement.dueDate != null
        ? String(state.agreement.dueDate).trim()
        : '';

    const agreementIdOverrideStr = agreementIdOverride != null && String(agreementIdOverride).trim() !== ''
        ? String(agreementIdOverride).trim()
        : '';
    const dueDateOverrideStr = dueDateOverride != null && String(dueDateOverride).trim() !== ''
        ? String(dueDateOverride).trim()
        : '';

    const agreementId = agreementIdOverrideStr || agreementIdFromState;
    const dueDateFromAgreement = agreementIdOverrideStr ? dueDateOverrideStr : dueDateFromState;
    const dueDate = sendWhatsapp ? moment().format('YYYY-MM-DD') : dueDateFromAgreement;

    const resp = await dispatch('meusContratos/ActionSendAgreementEmailOrWhatsappBoticario', {
        agreementId,
        dueDate,
        clientId,
        Email: sendEmail ? user?.Email : '',
        PhoneNumber: sendWhatsapp ? phoneForApi : '',
        OS: navigator.platform,
        IP: state.ipUser ?? '',
        UserID: state.userAdmId ?? userIDStorage,
        tag: localStorage.getItem('tag'),
        from,
        advisory: localStorage.getItem('advisory'),
    }, { root: true });

    const code = resp && typeof resp === 'object' && 'code' in resp ? resp.code : null;
    if (code !== null && code !== 200) {
        throw new Error(`Não foi possível fazer o envio por ${sendEmail ? 'e-mail' : 'WhatsApp'}`);
    }
    if (resp && resp.data && typeof resp.data.code !== 'undefined' && resp.data.code !== 200) {
        throw new Error(`Não foi possível fazer o envio por ${sendEmail ? 'e-mail' : 'WhatsApp'}`);
    }
};

export const ActionNegotiationLogPaymentAction = async ({ state, dispatch }, payload = {}) => {
    const brand = String(payload.brand || '').trim().toLowerCase();
    if (!['boticario', 'eudora'].includes(brand)) return;

    const agreement = payload.agreement || state.agreement || {};
    const prepared = payload.prepareToSetAgreement || state.prepareToSetAgreement || state.agreementBillContextItem || {};
    const agreementId = String(
        payload.agreementId
        || agreement.agreementId
        || prepared.agreementId
        || ''
    ).trim();
    const paymentMethod = payload.paymentMethod || state.selectedPaymentMethod || '';
    const paymentValue = payload.paymentValue || '';
    const userIDStorage = localStorage.getItem('UserID');
    const isEudora = brand === 'eudora';

    await dispatch('meusContratos/ActionPaymentActionBoticarioAndEudora', {
        action: payload.action,
        IP: state.ipUser ?? '',
        DeviceName: process.env.VUE_APP_API_DEVICE,
        OS: navigator.platform,
        UserID: state.userAdmId ?? userIDStorage,
        tag: localStorage.getItem('tag'),
        advisory: localStorage.getItem('advisory'),
        ...(isEudora
            ? { clientIdEudora: state.clientIdEudora }
            : { clientIdBoticario: state.clientIdBoticario }),
        agreementId,
        paymentMethod,
        paymentValue,
        product: prepared.product,
        contracts: prepared.contracts || prepared.installments,
        NotesJson: {
            action: payload.action,
            agreementId,
            paymentMethod,
            paymentValue,
            product: prepared.product,
            contracts: prepared.contracts || prepared.installments,
            loggedAt: new Date().toISOString(),
        },
    }, { root: true });
};

export const ActionNegotiationSetAgreementBillPaymentOption = ({ commit }, index) => {
    commit(types.SET_AGREEMENT_BILL_SELECTED_OPTION, index);
};

export const ActionNegotiationRequestAgreementBill = async (
    { commit, state },
    { brand, agreementItem },
) => {
    const ownerId = brand === 'eudora' ? 39 : 36;
    const clientId = brand === 'eudora' ? state.clientIdEudora : state.clientIdBoticario;

    if (clientId == null || String(clientId).trim() === '') {
        throw new Error('Identificador do cliente não encontrado. Recarregue a página e tente novamente.');
    }
    const agreementId = agreementItem && agreementItem.agreementId;
    if (agreementId == null || String(agreementId).trim() === '') {
        throw new Error('Número do acordo não encontrado.');
    }

    const dueDate = dueDateForAgreementBillApi(agreementItem);

    const billLoadingKey = `${brand}:${String(agreementId)}`;
    commit(types.SET_LOADING_AGREEMENT_BILL, billLoadingKey);
    try {
        const url = 'boticario-eudora/agreement-installments-bill';
        const body = {
            ownerId,
            httpOwnerId: ownerId,
            clientId: String(clientId),
            agreementId: String(agreementId),
            dueDate,
            IP: state.ipUser ?? '',
            OS: navigator.platform,
            UserID: state.userAdmId ?? localStorage.getItem('UserID'),
            tag: localStorage.getItem('tag'),
        };
        const { data } = await http.post(url, body);

        if (!data) {
            throw new Error('Resposta vazia da API.');
        }

        if (typeof data.code === 'number' && data.code !== 200) {
            throw new Error(
                typeof data.message === 'string' && data.message
                    ? data.message
                    : 'Não foi possível carregar as opções de pagamento.',
            );
        }

        if (data.error === true || data.error === 'true') {
            throw new Error(
                typeof data.message === 'string' && data.message
                    ? data.message
                    : 'Não foi possível carregar as opções de pagamento.',
            );
        }

        const envelope = data.results != null && typeof data.results === 'object'
            ? data.results
            : data;

        if (envelope.status !== true) {
            const msg = (typeof envelope.message === 'string' && envelope.message)
                || (typeof data.message === 'string' && data.message)
                || 'Não foi possível carregar as opções de pagamento.';
            throw new Error(msg);
        }

        const inner = envelope.response;
        if (!inner || inner.status !== true) {
            let msg = 'Resposta inválida da API de 2ª via.';
            if (inner && typeof inner.message === 'string' && inner.message) {
                msg = inner.message;
            } else if (inner && typeof inner.error === 'string' && inner.error) {
                msg = inner.error;
            }
            throw new Error(msg);
        }

        const options = buildAgreementBillOptions(inner);
        if (options.length === 0) {
            throw new Error('Nenhuma forma de pagamento disponível no momento.');
        }

        commit(types.SET_AGREEMENT_BILL_SCREEN, {
            brand,
            contextItem: agreementItem,
            rawInner: inner,
            options,
            selectedIndex: 0,
        });
        commit(types.SET_VIEW, 'agreementBillDetail');
    } catch (e) {
        let message = 'Não foi possível carregar a 2ª via. Tente novamente.';
        if (e instanceof Error && e.message) {
            message = e.message;
        } else if (e && typeof e === 'object' && e.data) {
            const d = e.data;
            if (typeof d.message === 'string' && d.message) message = d.message;
            else if (typeof d.error === 'string' && d.error) message = d.error;
        }
        throw new Error(message);
    } finally {
        commit(types.SET_LOADING_AGREEMENT_BILL, null);
    }
};

/**
 * Volta para o dashboard e sempre refaz `boticario-eudora/debts`.
 * Sem isso, a tela reaproveitaria dívidas/acordos em memória e poderia
 * mostrar dados que já mudaram no servidor (acordo fechado, título
 * baixado, oferta expirada).
 */
export const ActionNegotiationGoToDashboard = async ({ commit, dispatch }) => {
    commit(types.SET_LOADING_DASHBOARD_DEBTS, true);
    commit(types.RESET_NEGOTIATION_FLOW);
    try {
        await dispatch('ActionNegotiationGetDebts', null);
    } catch {
        return;
    } finally {
        commit(types.SET_LOADING_DASHBOARD_DEBTS, false);
    }
};

export const ActionNegotiationLogout = async ({ dispatch }) => {
    await dispatch('auth/ActionDoLogout', null, { root: true });
};

export const ActionNegotiationResetSession = ({ commit }) => {
    commit(types.RESET_NEGOTIATION_SESSION);
};
